import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate } from 'react-router-dom';
import { useMediaQuery } from "react-responsive";

import { ErrorBoundary } from "./Common/ErrorBoundary/errorBoundary";
import Error404 from "./Common/Error/error404";
import { SimpleLoader } from "./Common/Loader/simpleLoader";
import { trackPageView } from "../Utils/Helpers/Metrika/metrika";
import { RedirectToGuest } from "./Common/Redirect/redirectToGuest.jsx";

// Layouts
import { Layout as LayoutAccount } from "./Account/Desktop/Layout/layout";
import { Layout as LayoutExtranet } from "./extranet/Layout/layout";
import { Layout as LayoutExtranetMobile } from "./extranet_mobile/Layout/layout";

// Auth
const Auth = lazy(() => import("./Account/Desktop/Content/Auth/auth"));

// Host components - Content wrappers
const HostContent = lazy(() => import("./extranet/Content/content.jsx"));
const HostMobileContent = lazy(() => import("./extranet_mobile/Content/content.jsx"));

// Host pages - Desktop
const Today = lazy(() => import("./extranet/Content/Today/today.jsx"));
const Properties = lazy(() => import("./extranet/Content/Properties/properties.jsx"));
const PropertyCreate = lazy(() => import("./extranet/Content/Properties/Create/create.js"));
const PropertyUpdate = lazy(() => import("./extranet/Content/Properties/Update/update.jsx"));
const PageAmenities = lazy(() => import("./extranet/Content/Properties/Update/PageAmenities/pageAmenities.jsx"));
const PageRules = lazy(() => import("./extranet/Content/Properties/Update/PageRules/pageRules.jsx"));
const Calendar = lazy(() => import("./extranet/Content/Calendar/calendar.jsx"));
const Inbox = lazy(() => import("./extranet/Content/Inbox/inbox.jsx"));
const AccountSettings = lazy(() => import("./extranet/Content/AccountSettings/accountSettings.jsx"));
const Bookings = lazy(() => import("./extranet/Content/Bookings/bookings.jsx"));
const Progress = lazy(() => import("./extranet/Content/Progress/progress.jsx"));
const CalendarSyncHelp = lazy(() => import("./Common/Pages/CalendarSyncHelp/calendarSyncHelp.jsx"));
const CalendarSyncSourceHelp = lazy(() => import("./Common/Pages/CalendarSyncHelp/calendarSyncSourceHelp.jsx"));
const CalendarSyncOwnLinkHelp = lazy(() => import("./Common/Pages/CalendarSyncHelp/calendarSyncOwnLinkHelp.jsx"));

// Host pages - Mobile
const TodayMobile = lazy(() => import("./extranet_mobile/Content/Today/today.jsx"));
const PropertiesMobile = lazy(() => import("./extranet_mobile/Content/Properties/properties.jsx"));
const PropertyCreateMobile = lazy(() => import("./extranet_mobile/Content/Properties/Create/create.jsx"));
const PropertyUpdateMobile = lazy(() => import("./extranet_mobile/Content/Properties/Update/update.jsx"));
const PageAmenitiesMobile = lazy(() => import("./extranet_mobile/Content/Properties/Update/PageAmenities/pageAmenities.jsx"));
const PageRulesMobile = lazy(() => import("./extranet_mobile/Content/Properties/Update/PageRules/pageRules.jsx"));
const CalendarMobile = lazy(() => import("./extranet_mobile/Content/Calendar/calendar.jsx"));
const InboxMobile = lazy(() => import("./extranet_mobile/Content/Inbox/inbox.jsx"));
const ChatMobile = lazy(() => import("./extranet_mobile/Content/Inbox/Chat/chat.jsx"));
const ProfileMobile = lazy(() => import("./extranet_mobile/Content/Profile/profile.jsx"));
const AccountSettingsMobile = lazy(() => import("./extranet_mobile/Content/AccountSettings/accountSettings.jsx"));
const BookingRequestMobile = lazy(() => import("./extranet_mobile/Content/Bookings/bookingRequest.jsx"));
const ProgressMobileReviews = lazy(() => import("./extranet_mobile/Content/Progress/Reviews/reviews.jsx"));
const EmailVerification = lazy(() => import("./Common/Email/emailVerification"));
const MagicLink = lazy(() => import("./Common/Auth/magicLink.jsx"));

export function App(props) {
    const isDesktop = useMediaQuery({ query: '(min-width: 748px)' })

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route>
                <Route path="/login" element={<LayoutAccount {...props} />}>
                    <Route index element={<Auth {...props} />} />
                    <Route path="sign-up" element={<Auth {...props} />} />
                    <Route path="register" element={<Auth {...props} />} />
                </Route>
                <Route path="/user/email-verify" element={<EmailVerification {...props} />} />
                <Route path="/auth/magic-link/:token" element={<MagicLink />} />
                <Route path="/" element={isDesktop ? <LayoutExtranet {...props} /> : <LayoutExtranetMobile {...props} />}>
                    <Route index element={<Navigate to="/today" replace />} />
                    <Route path="today" element={isDesktop ? <Today {...props} /> : <TodayMobile {...props} />} />
                    <Route path="properties" element={isDesktop ? <Properties {...props} /> : <PropertiesMobile {...props} />} />
                    <Route path="properties/create" element={isDesktop ? <PropertyCreate {...props} /> : <PropertyCreateMobile {...props} />} />
                    <Route path="properties/:id/update" element={isDesktop ? <PropertyUpdate {...props} /> : <PropertyUpdateMobile {...props} />} />
                    <Route path="properties/:id/update/amenities" element={isDesktop ? <PageAmenities {...props} /> : <PageAmenitiesMobile {...props} />} />
                    <Route path="properties/:id/update/rules" element={isDesktop ? <PageRules {...props} /> : <PageRulesMobile {...props} />} />
                    <Route path="calendar" element={isDesktop ? <Calendar {...props} /> : <CalendarMobile {...props} />} />
                    <Route path="calendar/:id" element={isDesktop ? <Calendar {...props} /> : <CalendarMobile {...props} />} />
                    <Route path="help/calendar-sync" element={<CalendarSyncHelp {...props} />} />
                    <Route path="help/calendar-sync/own-link" element={<CalendarSyncOwnLinkHelp {...props} />} />
                    <Route path="help/calendar-sync/:source" element={<CalendarSyncSourceHelp {...props} />} />
                    <Route path="inbox" element={isDesktop ? <Inbox {...props} /> : <InboxMobile {...props} />} />
                    <Route path="inbox/:id" element={isDesktop ? <Inbox {...props} /> : <ChatMobile {...props} />} />
                    <Route path="account-settings" element={isDesktop ? <AccountSettings {...props} /> : <AccountSettingsMobile {...props} />} />
                    <Route path="account-settings/personal-info" element={isDesktop ? <AccountSettings {...props} /> : <AccountSettingsMobile {...props} />} />
                    <Route path="account-settings/notifications" element={isDesktop ? <AccountSettings {...props} /> : <AccountSettingsMobile {...props} />} />
                    <Route path="account-settings/delete-account" element={isDesktop ? <AccountSettings {...props} /> : <AccountSettingsMobile {...props} />} />
                    <Route path="reservations" element={<RedirectToGuest />} />
                    {isDesktop && <Route path="bookings" element={<Bookings {...props} />} />}
                    {isDesktop && <Route path="booking-request/:status" element={<Bookings {...props} />} />}
                    {!isDesktop && <Route path="booking-request/:status" element={<BookingRequestMobile {...props} />} />}
                    {isDesktop && <Route path="progress/reviews" element={<Progress {...props} />} />}
                    {isDesktop && <Route path="progress/reviews/:id" element={<Progress {...props} />} />}
                    {!isDesktop && <Route path="progress/reviews" element={<ProgressMobileReviews {...props} />} />}
                    {!isDesktop && <Route path="progress/reviews/:id" element={<ProgressMobileReviews {...props} />} />}
                    {!isDesktop && <Route path="profile" element={<ProfileMobile {...props} />} />}
                    <Route path='*' element={<Error404 {...props} />} />
                </Route>
            </Route>
        )
    );

    useEffect(() => {
        trackPageView();
        return router.subscribe(trackPageView);
    }, [router]);

    return (
        <ErrorBoundary>
            <Suspense fallback={<SimpleLoader />}>
                <RouterProvider router={router} />
            </Suspense>
        </ErrorBoundary>
    );
}
